import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import RawHeader from "../components/RawHeader";
import ClothingViewer3D from "../components/ClothingViewer3D";
import ViewerErrorBoundary from "../components/ViewerErrorBoundary";
import DimensionTable from "../components/DimensionTable";
import { addToCart } from "../lib/cart";
import { useCustomer } from "../contexts/CustomerContext";
import { InfoFooter } from "./InfoPages";
import { customerApi } from "../lib/customerApi";
import { preOrderDeposit, preOrderBalance } from "../lib/preOrder";

const SIZES = ["S", "M", "L", "XL"];

// Lazily-initialised per-browser viewer id — persists across sessions.
const getViewerId = () => {
  try {
    let v = localStorage.getItem("raw_viewer_id");
    if (!v) {
      v = "v_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("raw_viewer_id", v);
    }
    return v;
  } catch {
    return "v_" + Math.random().toString(36).slice(2);
  }
};

const ProductDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [product, setProduct] = useState(null);
  const { customer } = useCustomer();
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState("M");
  const [qty, setQty] = useState(1);
  const [view, setView] = useState("3d"); // img | 3d — default to 3D rotating view for customers
  const [checkingOut, setCheckingOut] = useState(false);
  const [liveViewers, setLiveViewers] = useState(null);
  const [recentSales, setRecentSales] = useState(null);
  const [lightbox, setLightbox] = useState(null); // data-url string or null

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => toast.error("Product not found"))
      .finally(() => setLoading(false));
  }, [id]);

  // Check whether this product is in the signed-in customer's saved list
  useEffect(() => {
    if (!customer || !id) { setIsSaved(false); return; }
    customerApi.get("/my/saved").then((r) => {
      setIsSaved(Array.isArray(r.data) && r.data.some((x) => x.id === id));
    }).catch(() => {});
  }, [customer, id]);

  // Live-viewers: ping our presence + poll the count every 30s while this page is open
  useEffect(() => {
    if (!id) return;
    const vid = getViewerId();
    let cancelled = false;
    const pingAndCount = async () => {
      try {
        await api.post(`/products/${id}/view-ping`, { viewer_id: vid });
        const r = await api.get(`/products/${id}/live-viewers`);
        if (!cancelled) setLiveViewers(r.data?.count ?? null);
      } catch {
        // silent — live-viewer badge is pure nice-to-have
      }
    };
    pingAndCount();
    const t = setInterval(pingAndCount, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id]);

  // Recent-sales: how many units bought in the last 24 h (social proof)
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api.get(`/products/${id}/recent-sales`)
      .then((r) => { if (!cancelled) setRecentSales(r.data?.units_sold ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  const toggleSaved = async () => {
    if (!customer) {
      toast.info("Sign in to save items");
      nav("/login");
      return;
    }
    try {
      if (isSaved) {
        await customerApi.delete(`/my/saved/${id}`);
        setIsSaved(false);
        toast.success("Removed from saved");
      } else {
        await customerApi.post(`/my/saved/${id}`);
        setIsSaved(true);
        toast.success("Saved to your account");
      }
    } catch {
      toast.error("Couldn't update saved items");
    }
  };

  const buy = async () => {
    if (!product) return;
    setCheckingOut(true);
    try {
      const res = await api.post("/checkout/session", {
        product_id: product.id,
        size,
        quantity: qty,
        origin_url: window.location.origin,
      });
      if (!res?.data?.url) throw new Error("Payment processor did not return a checkout URL.");
      window.location.href = res.data.url;
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg = "Checkout failed";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw) && raw[0]?.msg) msg = `Invalid request: ${raw[0].msg}`;
      else if (e?.message) msg = e.message;
      toast.error(msg);
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen text-white">
        <RawHeader />
        <div className="text-center py-32 text-zinc-600 text-xs tracking-[0.3em] uppercase">LOADING...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen text-white">
        <RawHeader />
        <div className="max-w-[1600px] mx-auto px-6 py-32 text-center">
          <div className="text-[#d4af37] text-xs tracking-[0.3em] uppercase mb-4">// 404</div>
          <p className="text-zinc-500 mb-6">Product not found.</p>
          <Link to="/" className="btn-raw">← BACK TO SHOP</Link>
        </div>
      </div>
    );
  }

  const hasImage = !!product.image_base64;
  const imgSrc = hasImage
    ? (product.image_base64.startsWith("data:") ? product.image_base64 : `data:image/png;base64,${product.image_base64}`)
    : null;

  return (
    <div className="min-h-screen text-white">
      <RawHeader />

      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        <Link to="/" className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white inline-block mb-6" data-testid="back-link">
          ← BACK TO CATALOG
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
          {/* Left: visuals */}
          <div className="bg-[#050505] p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500">// VISUAL_OUTPUT</div>
              <div className="flex gap-1">
                <button
                  data-testid="view-img"
                  onClick={() => setView("img")}
                  className={`px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border transition-colors ${view === "img" ? "bg-white text-black border-white" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
                >
                  Photo
                </button>
                <button
                  data-testid="view-3d"
                  onClick={() => setView("3d")}
                  className={`px-3 py-1.5 text-[9px] tracking-[0.2em] uppercase border transition-colors ${view === "3d" ? "bg-white text-black border-white" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
                >
                  3D
                </button>
              </div>
            </div>

            <div className="aspect-square relative">
              {view === "3d" ? (
                <ViewerErrorBoundary>
                  <ClothingViewer3D
                    type={product.clothing_type}
                    color={product.color || "#d4af37"}
                    logoOverlay={product.logo_overlay}
                    imageSrc={imgSrc}
                    imageBack={product.image_back || null}
                    imageSide={product.image_side || null}
                    autoRotate
                  />
                </ViewerErrorBoundary>
              ) : hasImage ? (
                <div className="w-full h-full bg-[#0a0a0a] border border-zinc-800 relative grid-bg">
                  <img src={imgSrc} alt={product.name} className="absolute inset-0 w-full h-full object-contain" />
                  <div className="absolute top-3 left-3 text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-mono">
                    // PRODUCT_PHOTO.GENERATED
                  </div>
                </div>
              ) : (
                <ViewerErrorBoundary>
                  <ClothingViewer3D
                    type={product.clothing_type}
                    color={product.color || "#d4af37"}
                    logoOverlay={product.logo_overlay}
                    autoRotate
                  />
                </ViewerErrorBoundary>
              )}
            </div>

            {/* Multi-image gallery — extra detail shots */}
            {Array.isArray(product.gallery_images) && product.gallery_images.length > 0 && (
              <div data-testid="gallery-strip" className="pt-3 border-t border-zinc-800">
                <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-2">// DETAIL SHOTS ({product.gallery_images.length})</div>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {product.gallery_images.map((g, i) => {
                    const src = g.startsWith("data:") ? g : `data:image/png;base64,${g}`;
                    return (
                      <button
                        key={i}
                        type="button"
                        data-testid={`gallery-thumb-${i}`}
                        onClick={() => setLightbox(src)}
                        className="aspect-square bg-black border border-zinc-800 hover:border-[#d4af37] overflow-hidden group"
                      >
                        <img src={src} alt={`detail ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right: info + buy */}
          <div className="bg-[#050505] p-6 lg:p-10 flex flex-col gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3 text-[10px] tracking-[0.3em] uppercase flex-wrap">
                <span className="text-[#d4af37] border border-[#d4af37] px-2 py-0.5">{product.clothing_type}</span>
                {product.garment_style && (
                  <span className="text-white border border-zinc-700 px-2 py-0.5">{product.garment_style}</span>
                )}
                <span className="text-zinc-500">ID: {product.id.slice(0, 8).toUpperCase()}</span>
              </div>
              <h1 className="font-display text-4xl lg:text-5xl leading-none mb-3" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-sm text-zinc-400 leading-relaxed">
                {product.description || <span className="text-zinc-700">// No description.</span>}
              </p>
              {(liveViewers !== null && liveViewers > 1) || (recentSales !== null && recentSales > 0) ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {liveViewers !== null && liveViewers > 1 && (
                    <div
                      data-testid="live-viewers"
                      className="inline-flex items-center gap-2 border border-[#d4af37]/40 bg-[#d4af37]/5 px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase text-[#d4af37] font-mono"
                      title={`${liveViewers} viewers active in the last 2 minutes`}
                    >
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#d4af37] opacity-60 animate-ping"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#d4af37]"></span>
                      </span>
                      {liveViewers} {liveViewers === 1 ? "PERSON" : "PEOPLE"} VIEWING NOW
                    </div>
                  )}
                  {recentSales !== null && recentSales > 0 && (
                    <div
                      data-testid="recent-sales"
                      className="inline-flex items-center gap-2 border border-[#d4af37]/40 bg-[#d4af37]/5 px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase text-[#d4af37] font-mono"
                      title={`${recentSales} units sold in the last 24 hours`}
                    >
                      <svg
                        width="12"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="#d4af37"
                        aria-hidden="true"
                        className="drop-shadow-[0_0_4px_rgba(212,175,55,0.6)]"
                      >
                        <path d="M12 2s1.5 3 1.5 6-2 4-2 6 1 3 2.5 3 3-2 3-5c0 4.5-3.5 8-7 8-3.866 0-7-3.134-7-7 0-3 2-5.5 4-7 0 2 1.5 3 3 3 0-2-1-4 2-7z" />
                      </svg>
                      {recentSales} BOUGHT IN LAST 24H
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
              <div className="bg-[#050505] p-4">
                <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Material</div>
                <div className="font-mono text-sm">{product.material || "—"}</div>
              </div>
              <div className="bg-[#050505] p-4">
                <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Color</div>
                <div className="font-mono text-sm flex items-center gap-2">
                  {product.color && (
                    <span className="w-3 h-3 border border-zinc-600" style={{ background: product.color }} />
                  )}
                  {product.color || "—"}
                </div>
              </div>
              <div className="bg-[#050505] p-4">
                <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Full Price</div>
                <div className="font-mono text-lg text-zinc-300 line-through decoration-zinc-600">£{Number(product.price).toFixed(2)}</div>
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37] mt-1">Pre-order Deposit</div>
                <div className="font-mono text-2xl text-[#d4af37]">£{preOrderDeposit(product.price).toFixed(2)}</div>
              </div>
              <div className="bg-[#050505] p-4">
                <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-500 mb-1">Stock</div>
                <div className="font-mono text-sm">{product.stock > 0 ? `${product.stock} UNITS` : <span className="text-[#d4af37]">SOLD OUT</span>}</div>
              </div>
            </div>

            <div>
              <label className="label-raw">Select Size</label>
              <div className="grid grid-cols-4 gap-1">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-${s}`}
                    className={`py-4 font-display text-xl transition-colors border ${
                      size === s
                        ? "bg-[#d4af37] border-[#d4af37] text-black"
                        : "bg-transparent border-zinc-800 text-zinc-400 hover:border-white hover:text-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label-raw">Quantity</label>
              <div className="flex items-center border border-zinc-800 w-fit">
                <button
                  data-testid="qty-dec"
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-4 py-3 text-zinc-400 hover:text-white hover:bg-[#0a0a0a]"
                >
                  −
                </button>
                <span className="px-6 font-mono text-sm" data-testid="qty-val">{qty}</span>
                <button
                  data-testid="qty-inc"
                  onClick={() => setQty(Math.min(10, qty + 1))}
                  className="px-4 py-3 text-zinc-400 hover:text-white hover:bg-[#0a0a0a]"
                >
                  +
                </button>
              </div>
            </div>

            <div className="flex gap-2 sm:gap-3">
              <button
                data-testid="add-to-cart-button"
                onClick={() => {
                  addToCart(product, size, qty);
                  toast.success("Added to basket");
                }}
                disabled={product.stock <= 0}
                className="btn-raw flex-1 py-4 text-sm tracking-[0.3em] disabled:opacity-40"
              >
                {product.stock <= 0 ? "SOLD OUT" : "+ ADD TO BASKET"}
              </button>
              <button
                data-testid="save-item-btn"
                onClick={toggleSaved}
                className={`px-4 border transition-colors ${
                  isSaved
                    ? "bg-[#d4af37] border-[#d4af37] text-black"
                    : "border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-black"
                }`}
                aria-label={isSaved ? "Remove saved" : "Save to account"}
                title={isSaved ? "Saved to your account" : "Save for later"}
              >
                {isSaved ? "♥" : "♡"}
              </button>
            </div>
            <button
              data-testid="buy-now-button"
              onClick={() => {
                addToCart(product, size, qty);
                nav("/cart");
              }}
              disabled={product.stock <= 0}
              className="btn-raw w-full py-3 text-[11px] tracking-[0.3em] disabled:opacity-40"
            >
              {product.stock <= 0 ? "" : "→ VIEW BASKET"}
            </button>
            <button
              data-testid="buy-button"
              onClick={buy}
              disabled={checkingOut || product.stock <= 0}
              className="btn-raw btn-raw-primary w-full py-5 text-sm tracking-[0.3em]"
            >
              {checkingOut ? "PROCESSING..." : product.stock <= 0 ? "SOLD OUT" : `→ PRE-ORDER · PAY DEPOSIT £${(preOrderDeposit(product.price) * qty).toFixed(2)}`}
            </button>
            <div className="border-2 border-[#d4af37] bg-[#0a0a0a] p-3 text-[10px] font-mono space-y-1">
              <div className="flex justify-between text-[#d4af37] font-bold">
                <span>PRE-ORDER DEPOSIT (INC VAT)</span>
                <span>£{(preOrderDeposit(product.price) * qty).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-zinc-500 text-[9px]">
                <span>· of which VAT (20%)</span>
                <span>£{((preOrderDeposit(product.price) * qty) - (preOrderDeposit(product.price) * qty) / 1.20).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-zinc-400 border-t border-zinc-800 pt-1 mt-1">
                <span>SHIPPING</span>
                <span className="text-[9px]">from £4.95 UK · £14.95 int'l</span>
              </div>
              <div className="flex justify-between text-white pt-1 border-t border-zinc-800 mt-1">
                <span>BALANCE DUE ON DISPATCH</span>
                <span className="text-zinc-300">£{(preOrderBalance(product.price) * qty).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-white font-bold pt-1 border-t border-zinc-800 mt-1">
                <span>FULL ITEM PRICE</span>
                <span className="text-[#d4af37]">£{(product.price * qty).toFixed(2)}</span>
              </div>
            </div>
            <div className="text-[9px] tracking-[0.3em] uppercase text-zinc-600 text-center">
              PRE-ORDER · DEPOSIT NOW · BALANCE ON DISPATCH · STRIPE
            </div>
          </div>
        </div>

        {/* Dimensions */}
        <div className="mt-12">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">// SIZE_SPECIFICATIONS</div>
              <h2 className="font-display text-2xl">Dimensions</h2>
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">ALL MEASUREMENTS IN CM</div>
          </div>
          <DimensionTable dimensions={product.dimensions} onChange={() => {}} readOnly />
        </div>

        {/* Material Composition */}
        {product.materials && product.materials.length > 0 && (
          <div className="mt-12">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1">// FABRIC_MAP</div>
                <h2 className="font-display text-2xl">Material Composition</h2>
              </div>
            </div>
            <div className="border border-zinc-800" data-testid="product-materials">
              <div className="grid grid-cols-[200px_1fr] text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-bold border-b border-zinc-800 bg-[#0a0a0a]">
                <div className="p-3 border-r border-zinc-800">PANEL</div>
                <div className="p-3">MATERIAL / FABRIC</div>
              </div>
              {product.materials.map((m, i) => (
                <div key={i} className="grid grid-cols-[200px_1fr] border-b border-zinc-800 last:border-b-0 hover:bg-[#0a0a0a]/50">
                  <div className="p-3 border-r border-zinc-800 font-mono text-sm text-[#d4af37]">{m.panel}</div>
                  <div className="p-3 font-mono text-sm text-zinc-300">{m.material || <span className="text-zinc-600">—</span>}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Gallery lightbox */}
      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <img src={lightbox} alt="detail" className="max-w-[95vw] max-h-[95vh] object-contain border border-[#d4af37]" />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 text-[#d4af37] text-2xl leading-none hover:text-white"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
      )}
      <InfoFooter />
    </div>
  );
};

export default ProductDetail;
